import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { superAdminAPI } from '../lib/api'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog'
import { toast } from 'sonner'
import {
  Shield, Building2, Users, TrendingUp, Globe, Loader2, LogIn, LogOut,
  Plus, Pause, Play, Settings, Layers, ChevronRight, BarChart3, Coins
} from 'lucide-react'

export default function SuperAdminPage() {
  const [token, setToken] = useState(localStorage.getItem('sa_token') || '')
  const [isAuth, setIsAuth] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [logging, setLogging] = useState(false)

  const [stats, setStats] = useState(null)
  const [tenants, setTenants] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [selectedTenant, setSelectedTenant] = useState(null)
  const [newTenant, setNewTenant] = useState({ name: '', slug: '', plan: 'starter', owner_email: '', owner_name: '', owner_password: '', company_name: '', country: '' })

  useEffect(() => {
    if (token) verifyAndLoad()
  }, [])

  const verifyAndLoad = async (authToken) => {
    const t = authToken || token
    if (!t) return
    setLoading(true)
    try {
      const [statsRes, tenantsRes] = await Promise.all([
        superAdminAPI.getStats(t),
        superAdminAPI.listTenants(t),
      ])
      setStats(statsRes.data)
      setTenants(tenantsRes.data.tenants)
      setTotal(tenantsRes.data.total)
      setIsAuth(true)
    } catch {
      setToken('')
      localStorage.removeItem('sa_token')
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setLogging(true)
    try {
      const res = await superAdminAPI.login({ email, password })
      const t = res.data.access_token
      setToken(t)
      localStorage.setItem('sa_token', t)
      setIsAuth(true)
      await verifyAndLoad(t)
    } catch {
      toast.error('Invalid credentials')
    } finally {
      setLogging(false)
    }
  }

  const handleLogout = () => {
    setToken('')
    setIsAuth(false)
    localStorage.removeItem('sa_token')
  }

  const handleCreateTenant = async () => {
    try {
      const t = token || localStorage.getItem('sa_token')
      await superAdminAPI.createTenant(t, newTenant)
      toast.success('Tenant created!')
      setShowCreate(false)
      setNewTenant({ name: '', slug: '', plan: 'starter', owner_email: '', owner_name: '', owner_password: '', company_name: '', country: '' })
      verifyAndLoad(t)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create tenant')
    }
  }

  const handleSuspend = async (id) => {
    const t = token || localStorage.getItem('sa_token')
    await superAdminAPI.suspendTenant(t, id)
    toast.success('Tenant suspended')
    verifyAndLoad(t)
  }

  const handleActivate = async (id) => {
    const t = token || localStorage.getItem('sa_token')
    await superAdminAPI.activateTenant(t, id)
    toast.success('Tenant activated')
    verifyAndLoad(t)
  }

  const viewTenant = async (id) => {
    try {
      const t = token || localStorage.getItem('sa_token')
      const res = await superAdminAPI.getTenant(t, id)
      setSelectedTenant(res.data)
    } catch { toast.error('Failed to load tenant') }
  }

  // Login Screen
  if (!isAuth) {
    return (
      <div className="min-h-screen bg-brand-secondary flex items-center justify-center" data-testid="super-admin-login">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md bg-white/5 border border-white/10 p-8">
          <div className="flex items-center gap-3 mb-8">
            <Shield className="w-8 h-8 text-brand-primary" />
            <div>
              <h1 className="text-2xl font-heading text-white">Super Admin</h1>
              <p className="text-white/50 text-sm">2kenize Platform Control</p>
            </div>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white/70">Email</Label>
              <Input value={email} onChange={e => setEmail(e.target.value)} type="email" required
                className="h-12 bg-white/5 border-white/10 text-white rounded-none" data-testid="sa-email" />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Password</Label>
              <Input value={password} onChange={e => setPassword(e.target.value)} type="password" required
                className="h-12 bg-white/5 border-white/10 text-white rounded-none" data-testid="sa-password" />
            </div>
            <Button type="submit" disabled={logging}
              className="w-full h-12 bg-brand-primary hover:bg-brand-primary-hover text-brand-secondary font-semibold rounded-none" data-testid="sa-login-btn">
              {logging ? <Loader2 className="w-5 h-5 animate-spin" /> : <><LogIn className="w-4 h-4 mr-2" /> Sign In</>}
            </Button>
          </form>
        </motion.div>
      </div>
    )
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="super-admin-dashboard">
      {/* Header */}
      <header className="bg-brand-secondary border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-brand-primary" />
            <span className="text-white font-heading text-lg">2kenize Super Admin</span>
          </div>
          <Button onClick={handleLogout} variant="ghost" className="text-white/60 hover:text-white hover:bg-white/10 rounded-none" data-testid="sa-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-primary" /></div>
        ) : (
          <>
            {/* Stats */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Total Tenants', value: stats?.total_tenants || 0, icon: Layers, accent: 'text-brand-primary' },
                { label: 'Active Tenants', value: stats?.active_tenants || 0, icon: Play, accent: 'text-emerald-500' },
                { label: 'Total Users', value: stats?.total_users || 0, icon: Users, accent: 'text-blue-500' },
                { label: 'Total Properties', value: stats?.total_properties || 0, icon: Building2, accent: 'text-purple-500' },
              ].map((s, i) => (
                <div key={i} className="bg-white border border-slate-100 p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-500 text-xs uppercase tracking-wider font-semibold">{s.label}</span>
                    <s.icon className={`w-5 h-5 ${s.accent}`} />
                  </div>
                  <p className="text-3xl font-heading text-brand-secondary font-mono" data-testid={`sa-stat-${i}`}>{s.value}</p>
                </div>
              ))}
            </motion.div>

            {/* Plans breakdown */}
            {stats?.plans && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                className="bg-white border border-slate-100 p-6 mb-8">
                <h3 className="text-brand-secondary font-heading text-lg mb-4">Plans Distribution</h3>
                <div className="flex gap-6">
                  {Object.entries(stats.plans).map(([plan, count]) => (
                    <div key={plan} className="flex items-center gap-2">
                      <div className={`w-3 h-3 ${plan === 'institutional' ? 'bg-brand-primary' : plan === 'pro' ? 'bg-blue-500' : 'bg-slate-400'}`} />
                      <span className="text-sm text-slate-600 capitalize">{plan}: <strong>{String(count)}</strong></span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Tenants Header */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-heading text-brand-secondary">Tenants ({total})</h2>
              <Button onClick={() => setShowCreate(true)}
                className="bg-brand-primary hover:bg-brand-primary-hover text-brand-secondary font-semibold rounded-none" data-testid="create-tenant-btn">
                <Plus className="w-4 h-4 mr-2" /> New Tenant
              </Button>
            </motion.div>

            {/* Tenants List */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="bg-white border border-slate-100 overflow-hidden">
              <table className="w-full">
                <thead className="bg-[#F8FAFC]">
                  <tr>
                    <th className="text-left p-4 text-xs text-slate-500 font-semibold uppercase tracking-wider">Tenant</th>
                    <th className="text-left p-4 text-xs text-slate-500 font-semibold uppercase tracking-wider">Plan</th>
                    <th className="text-left p-4 text-xs text-slate-500 font-semibold uppercase tracking-wider">Status</th>
                    <th className="text-left p-4 text-xs text-slate-500 font-semibold uppercase tracking-wider">Users</th>
                    <th className="text-left p-4 text-xs text-slate-500 font-semibold uppercase tracking-wider">Properties</th>
                    <th className="text-left p-4 text-xs text-slate-500 font-semibold uppercase tracking-wider">Raised</th>
                    <th className="text-right p-4 text-xs text-slate-500 font-semibold uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tenants.map(t => (
                    <tr key={t.id} className="hover:bg-[#F8FAFC]" data-testid={`tenant-row-${t.slug}`}>
                      <td className="p-4">
                        <p className="text-brand-secondary font-semibold">{t.name}</p>
                        <p className="text-xs text-slate-400">{t.slug}.2kenize.com</p>
                      </td>
                      <td className="p-4">
                        <span className={`inline-block px-2 py-0.5 text-xs font-semibold uppercase tracking-wider ${
                          t.plan === 'institutional' ? 'bg-brand-primary/20 text-brand-primary' : t.plan === 'pro' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                        }`}>{t.plan}</span>
                      </td>
                      <td className="p-4">
                        <span className={`inline-block px-2 py-0.5 text-xs font-semibold uppercase ${
                          t.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                        }`}>{t.status}</span>
                      </td>
                      <td className="p-4 text-sm text-slate-600 font-mono">{t.stats?.users || 0}</td>
                      <td className="p-4 text-sm text-slate-600 font-mono">{t.stats?.properties || 0}</td>
                      <td className="p-4 text-sm text-slate-600 font-mono">${(t.stats?.total_raised || 0).toLocaleString()}</td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => viewTenant(t.id)}
                            className="text-slate-500 hover:text-brand-secondary" data-testid={`view-${t.slug}`}>
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                          {t.status === 'active' ? (
                            <Button size="sm" variant="ghost" onClick={() => handleSuspend(t.id)}
                              className="text-red-500 hover:bg-red-50" data-testid={`suspend-${t.slug}`}>
                              <Pause className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button size="sm" variant="ghost" onClick={() => handleActivate(t.id)}
                              className="text-emerald-500 hover:bg-emerald-50" data-testid={`activate-${t.slug}`}>
                              <Play className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </motion.div>
          </>
        )}
      </main>

      {/* Create Tenant Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg bg-white border-slate-100 rounded-none" data-testid="create-tenant-dialog">
          <DialogHeader>
            <DialogTitle className="text-brand-secondary font-heading">Create New Tenant</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-600 font-semibold">Name</Label>
              <Input value={newTenant.name} onChange={e => setNewTenant(p => ({ ...p, name: e.target.value }))}
                className="h-10 rounded-none" data-testid="new-tenant-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600 font-semibold">Slug</Label>
              <Input value={newTenant.slug} onChange={e => setNewTenant(p => ({ ...p, slug: e.target.value }))}
                placeholder="my-company" className="h-10 rounded-none" data-testid="new-tenant-slug" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600 font-semibold">Plan</Label>
              <select value={newTenant.plan} onChange={e => setNewTenant(p => ({ ...p, plan: e.target.value }))}
                className="w-full h-10 border border-slate-200 px-3 text-sm" data-testid="new-tenant-plan">
                <option value="starter">Starter</option>
                <option value="pro">Pro</option>
                <option value="institutional">Institutional</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600 font-semibold">Country</Label>
              <Input value={newTenant.country} onChange={e => setNewTenant(p => ({ ...p, country: e.target.value }))}
                className="h-10 rounded-none" data-testid="new-tenant-country" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600 font-semibold">Owner Email</Label>
              <Input value={newTenant.owner_email} onChange={e => setNewTenant(p => ({ ...p, owner_email: e.target.value }))}
                type="email" className="h-10 rounded-none" data-testid="new-tenant-email" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600 font-semibold">Owner Password</Label>
              <Input value={newTenant.owner_password} onChange={e => setNewTenant(p => ({ ...p, owner_password: e.target.value }))}
                type="password" className="h-10 rounded-none" data-testid="new-tenant-password" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600 font-semibold">Owner Name</Label>
              <Input value={newTenant.owner_name} onChange={e => setNewTenant(p => ({ ...p, owner_name: e.target.value }))}
                className="h-10 rounded-none" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600 font-semibold">Company</Label>
              <Input value={newTenant.company_name} onChange={e => setNewTenant(p => ({ ...p, company_name: e.target.value }))}
                className="h-10 rounded-none" />
            </div>
          </div>
          <Button onClick={handleCreateTenant}
            className="w-full mt-4 h-12 bg-brand-primary hover:bg-brand-primary-hover text-brand-secondary font-semibold rounded-none" data-testid="submit-create-tenant">
            <Plus className="w-4 h-4 mr-2" /> Create Tenant
          </Button>
        </DialogContent>
      </Dialog>

      {/* Tenant Detail Dialog */}
      <Dialog open={!!selectedTenant} onOpenChange={() => setSelectedTenant(null)}>
        <DialogContent className="sm:max-w-2xl bg-white border-slate-100 rounded-none" data-testid="tenant-detail-dialog">
          {selectedTenant && (
            <>
              <DialogHeader>
                <DialogTitle className="text-brand-secondary font-heading">{selectedTenant.name}</DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-[#F8FAFC] p-4">
                    <span className="text-xs text-slate-500 uppercase font-semibold">Plan</span>
                    <p className="text-lg font-heading text-brand-secondary capitalize">{selectedTenant.plan}</p>
                  </div>
                  <div className="bg-[#F8FAFC] p-4">
                    <span className="text-xs text-slate-500 uppercase font-semibold">Status</span>
                    <p className={`text-lg font-heading ${selectedTenant.status === 'active' ? 'text-emerald-600' : 'text-red-600'}`}>{selectedTenant.status}</p>
                  </div>
                  <div className="bg-[#F8FAFC] p-4">
                    <span className="text-xs text-slate-500 uppercase font-semibold">Country</span>
                    <p className="text-lg font-heading text-brand-secondary">{selectedTenant.country || 'N/A'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-[#F8FAFC] p-4">
                    <span className="text-xs text-slate-500 uppercase font-semibold">Users</span>
                    <p className="text-2xl font-mono text-brand-secondary">{selectedTenant.stats?.users || 0}</p>
                  </div>
                  <div className="bg-[#F8FAFC] p-4">
                    <span className="text-xs text-slate-500 uppercase font-semibold">Properties</span>
                    <p className="text-2xl font-mono text-brand-secondary">{selectedTenant.stats?.properties || 0}</p>
                  </div>
                  <div className="bg-[#F8FAFC] p-4">
                    <span className="text-xs text-slate-500 uppercase font-semibold">Raised</span>
                    <p className="text-2xl font-mono text-brand-secondary">${(selectedTenant.stats?.total_raised || 0).toLocaleString()}</p>
                  </div>
                </div>
                {selectedTenant.domains?.length > 0 && (
                  <div>
                    <h4 className="text-sm text-slate-500 uppercase font-semibold mb-2">Domains</h4>
                    <div className="space-y-1">
                      {selectedTenant.domains.map(d => (
                        <div key={d.id} className="flex items-center gap-2 text-sm">
                          <Globe className="w-4 h-4 text-brand-primary" />
                          <span className="text-brand-secondary">{d.domain}</span>
                          <span className={`px-1 text-xs ${d.verified ? 'text-emerald-600' : 'text-amber-600'}`}>
                            {d.verified ? 'verified' : 'pending'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <span>Owner: {selectedTenant.owner_email}</span>
                  <span>&middot;</span>
                  <span>Created: {new Date(selectedTenant.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
